import { useEffect, useState } from "react";
import { showToast } from "../../utils/showToast";
import { getAllowParticipants } from "../../api/rooms/getAllowParticipant";
import { useInfo } from "../../hooks/useInfo";
import { deletePatientToQueue } from "../../api/rooms/deletePatientToQueue";
import styled from "styled-components";
import { Button, IconButton, Paper } from "@mui/material";
import { MdClose } from "react-icons/md";
import { getParticipants } from "../../api/rooms/getParticipants";

const Container = styled("div")`
  display: flex;
  box-sizing: border-box;
  padding: 6px;
  height: 100%;
  min-width: 350px;
  z-index: auto;

  @container (max-width: 1100px) {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 100;
  }

  @container (max-width: 950px) {
    width: 100%;
    min-width: 100%;
  }
`;

const ListWrapper = styled(Paper)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-flow: column;
  box-sizing: border-box;
`;

const PatientItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
`;

const SmallButton = styled(Button)`
  height: 30px;
  width: 10px;
  font-size: 0.875rem;
`;

const PatientWrapper = styled("div")`
  padding: 12px;
`;

const HeaderWrapper = styled("div")`
  height: 40px;
  display: flex;
  justify-content: center;
  position: relative;
  color: ${(props) => props.theme.colors.secondary};

  &::after {
    content: "";
    display: block;
    position: absolute;
    height: 1px;
    width: 90%;
    bottom: 0;
    background-color: ${(props) => props.theme.colors.secondary};
  }
`;

const Title = styled("span")`
  display: inherit;
  align-items: center;
  gap: 10px;
  font-size: 1.2rem;
  line-height: 1.2;

  .svg {
    fill: ${(props) => props.theme.colors.secondary};
  }
`;

const CloseBtn = styled(IconButton)`
  && {
    position: absolute;
    right: 5px;
    top: 5px;
    display: flex;
    align-items: center;
    justify-self: center;
    height: 30px;
    width: 30px;
    min-width: fit-content;
    padding: 5px;
  }
`;
interface WaitingPatient {
  patientId: number;
  name: string;
}

interface RoomParticipant {
  role: string;
  connectedAt: string;
  status: "connected" | "disconnected";
  name: string;
}

interface WaitingListProps {
  onToggleWaitingList: () => void;
}

const PATIENT_NAME_KEY = "connected_patient_name";
const PROFESSIONAL_NAME_KEY = "connected_professional_name";

const saveToStorage = (key: string, value: string | null) => {
  if (value) {
    sessionStorage.setItem(key, value);
  } else {
    sessionStorage.removeItem(key);
  }
};

const loadFromStorage = (key: string): string | null => {
  return sessionStorage.getItem(key);
};

export const WaitingList = ({ onToggleWaitingList }: WaitingListProps) => {
  const [participants, setParticipants] = useState<WaitingPatient[]>([]);
  const [acceptedPatients, setAcceptedPatients] = useState<WaitingPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [latestPatientName, setLatestPatientName] = useState<string | null>(null);
  const [professionalName, setProfessionalName] = useState<string | null>(null);
  const { telemedicineData } = useInfo();

  useEffect(() => {
    setLatestPatientName(loadFromStorage(PATIENT_NAME_KEY));
    setProfessionalName(loadFromStorage(PROFESSIONAL_NAME_KEY));
    fetchWaitingPatients();

    const interval = setInterval(() => {
      fetchWaitingPatients();
      checkDisconnectedPatient();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const fetchWaitingPatients = async () => {
    try {
      const data = await getAllowParticipants(telemedicineData.id);
      setParticipants(data.patients || []);
    } catch (err) {
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  const checkDisconnectedPatient = async () => {
    try {
      const roomParticipants: RoomParticipant[] = await getParticipants();
      const connectedPatients = roomParticipants
        .filter((p) => p.role === "patient")
        .sort((a, b) => new Date(b.connectedAt).getTime() - new Date(a.connectedAt).getTime());

      const currentPatientName =
        connectedPatients.length > 0 ? connectedPatients[0].name : null;

      const professional = roomParticipants.find((p) => p.role === "professional");
      const currentProfessionalName = professional ? professional.name : null;

      setLatestPatientName(currentPatientName);
      setProfessionalName(currentProfessionalName);

      saveToStorage(PATIENT_NAME_KEY, currentPatientName);
      saveToStorage(PROFESSIONAL_NAME_KEY, currentProfessionalName);

      if (connectedPatients.length === 0 || connectedPatients[0].status === "disconnected") {
        setAcceptedPatients([]);
        saveToStorage(PATIENT_NAME_KEY, null);
        saveToStorage(PROFESSIONAL_NAME_KEY, null);
      }
    } catch (err) {
      console.error("Erro ao verificar desconexão de paciente:", err);
    }
  };

  const handleAccept = async (participant: WaitingPatient) => {
    try {
      await getAllowParticipants(telemedicineData.id);
      await deletePatientToQueue(telemedicineData.id, String(participant.patientId));

      const updated = [...acceptedPatients, participant];
      setAcceptedPatients(updated);

      setParticipants((prev) =>
        prev.filter((p) => p.patientId !== participant.patientId)
      );

      showToast("success", { text: `${participant.name} entrou na sala.` });
    } catch (err) {
      showToast("error", { text: "Erro ao tentar liberar o paciente." });
    }
  };

  const hasAnyone = participants.length > 0 || acceptedPatients.length > 0 || latestPatientName || professionalName;

  return (
    <Container>
      <ListWrapper elevation={14}>
        <HeaderWrapper>
          <Title>Participantes</Title>
          <CloseBtn onClick={onToggleWaitingList}>
            <MdClose size={22} />
          </CloseBtn>
        </HeaderWrapper>
        <PatientWrapper>
          {hasAnyone ? (
            <>
              {participants.map((participant) => (
                <PatientItem key={participant.patientId}>
                  <strong>{participant.name}</strong>
                  <SmallButton
                    sx={{
                      backgroundColor: "#28a745",
                      width: "100px",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#218838",
                      },
                    }}
                    onClick={() => handleAccept(participant)}
                  >
                    <strong>Aceitar</strong>
                  </SmallButton>
                </PatientItem>
              ))}
              {professionalName && (
                <PatientItem>
                  <strong>{professionalName}</strong>
                </PatientItem>
              )}
              {latestPatientName && (
                <PatientItem>
                  <strong>{latestPatientName}</strong>
                </PatientItem>
              )}
            </>
          ) : (
            <p style={{ textAlign: "center", color: "#666" }}>
              Nenhum paciente na fila de espera no momento.
            </p>
          )}
        </PatientWrapper>
      </ListWrapper>
    </Container>
  );
};
